import React, { createContext, useContext, useEffect, useState } from "react";

const LangContext = createContext(null);

const dict = {
  en: {
    nav: { booking: "Booking", schedules: "Schedules", announcements: "Announcements", programs: "Programs", contact: "Contact", portal: "Student Portal", admin: "Admin", login: "Login", admin_login: "Admin", staff_login: "Staff", logout: "Logout" },
    hero: {
      eyebrow: "DAWNVILLE · ENGLISH STUDIO · EST. 2018",
      title_a: "English, taught the way",
      title_b: "real thinkers learn it.",
      sub: "A boutique online studio preparing students from Primary through A-Level, IB and Cambridge KET/PET/FCE — with small cohorts, transparent progress, and faculty who actually return your emails.",
      cta_book: "Book a trial class",
      cta_explore: "Explore programs",
    },
    booking: {
      eyebrow: "01 · CLASS BOOKING",
      title: "Book your first conversation",
      sub: "Choose an interview, a free trial class, or a learning consultant call. We confirm by email within one business day.",
      tab_interview: "Interview",
      tab_trial: "Trial class",
      tab_consultant: "Learning consultant",
      name: "Full name",
      email: "Email",
      phone: "Phone or WeChat",
      program: "Program of interest",
      date: "Preferred date",
      time: "Preferred time",
      notes: "Anything we should know? (optional)",
      submit: "Confirm booking",
      success: "Booking received — we will reach out shortly.",
      pick_date: "Pick a date",
      pick_date_first: "Pick a date first — we'll show the slots open that day.",
      no_slots_day: "No bookings on {day}. Pick another day or contact us via WeChat.",
      no_slots_global: "No slots configured yet. Contact us via WeChat / Line for a time.",
      cal: {
        holiday: "National holiday",
        off_day: "Studio off-day",
        activity: "School activity",
        full_week: "Interview slots full this week",
        legend_available: "Available",
        legend_holiday: "National holiday (closed)",
        legend_off: "Dawnville off-day",
        legend_activity: "School activity",
        legend_full: "Interview week full (max {cap})",
      },
      thanks_eyebrow: "BOOKING RECEIVED",
      thanks_title: "Thanks — we've got it.",
      thanks_body: "A teacher will reach out within one business day on WeChat / Line / email to confirm the time. While you wait, take your free planner.",
      book_another: "Book another session",
      gift_eyebrow: "FREE WHILE YOU WAIT",
      gift_title: "30-Day Reading Challenge",
      gift_body: "A printable plan with daily prompts and a starter book list — get a head start before your trial class.",
      gift_btn: "Download my planner (PDF)",
      gift_hint: "Tailored to your selected program: {program}.",
    },
    schedules: {
      eyebrow: "02 · CURRENT TERM",
      title: "Live class schedule",
      sub: "Spots highlighted are open to new students this term.",
      th_class: "Class", th_program: "Program", th_teacher: "Teacher", th_day: "Day", th_time: "Time", th_level: "Level", th_seats: "Open seats",
      open: "Open",
      full: "Full",
    },
    announcements: {
      eyebrow: "03 · ANNOUNCEMENT BOULEVARD",
      title: "What's posted on the wall",
      sub: "Studio news, calendar updates, and notes from faculty.",
      tab_public: "Public",
      tab_staff: "Staff",
      tab_students: "Students & Parents",
      empty: "No announcements yet.",
    },
    programs: {
      eyebrow: "04 · ACADEMIC PROGRAMS",
      title: "Programs designed around an exam, a level, or a milestone.",
      sub: "Each track is led by a subject lead with current examiner training.",
    },
    contact: {
      eyebrow: "05 · CONTACT",
      title: "Talk to a real person.",
      address_label: "Studio", wechat_label: "WeChat", line_label: "Line", email_label: "Email", hours_label: "Hours",
      recommended: "Recommended",
      address: "Unit 4B, 88 Lantern Street, Online & Hybrid",
      wechat: "TwT02648",
      line: "misupig0423",
      email: "dawnvilleas2026@gmail.com",
      hours: "Mon–Sat · 09:00 – 20:00",
    },
    auth: {
      login_title: "Welcome back",
      login_sub: "Sign in to access the Dawnville student portal.",
      admin_login_title: "Admin sign in",
      admin_login_sub: "Manage announcements, schedules, bookings, leads, and audit events.",
      register_title: "Create a Dawnville account",
      register_sub: "For students and parents — full access to the portal.",
      email: "Email",
      password: "Password",
      name: "Full name",
      role: "I am a…",
      role_student: "Student",
      role_parent: "Parent",
      submit_login: "Sign in",
      submit_register: "Create account",
      switch_to_register: "Need an account? Register",
      switch_to_login: "Already have an account? Sign in",
      forgot_link: "Forgot your password?",
      bad_creds: "Invalid email or password.",
    },
    forgot: {
      title: "Reset your password",
      sub: "Enter your account email. If it's registered, we'll generate a reset link (the studio admin can also retrieve it from server logs).",
      submit: "Send reset link",
      sent_title: "Check your inbox",
      sent_body: "If that email is registered, a reset link has been issued. The link expires in 1 hour.",
      back_to_login: "Back to sign in",
    },
    reset: {
      title: "Set a new password",
      sub: "Choose a new password — at least 6 characters.",
      new_password: "New password",
      confirm_password: "Confirm password",
      submit: "Update password",
      success: "Password updated. Please sign in.",
      mismatch: "Passwords do not match.",
      too_short: "Password must be at least 6 characters.",
      no_token: "Missing or invalid reset token.",
    },
    admin: {
      title: "Admin · Announcement panel",
      sub: "Post a new announcement. Choose the audience board.",
      title_field: "Title (English)",
      title_zh_field: "Title (中文, optional)",
      content_field: "Content (English)",
      content_zh_field: "Content (中文, optional)",
      audience: "Audience board",
      audience_public: "Public",
      audience_staff: "Staff",
      audience_students: "Students & Parents",
      post: "Post announcement",
      recent: "Recent posts",
      delete: "Delete",
      bookings: "Recent bookings",
      tab_ann: "Announcements",
      tab_schedules: "Schedules",
      tab_course_calendar: "Course calendar",
      tab_bookings: "Bookings",
      tab_slots: "Booking slots",
      tab_calendar: "Calendar",
      tab_hero: "Home content",
      tab_programs: "Programs",
      tab_gallery: "Gallery",
      tab_achievements: "Achievements",
      tab_news: "News",
      tab_planners: "Planners",
      tab_cefr: "CEFR Test",
      tab_students: "Students",
      tab_notices: "Notices",
      tab_mock: "Mock tests",
      tab_staff: "Staff",
      tab_payslips: "Payslips",
      tab_leads: "Leads",
      tab_audit: "Audit log",
    },
    common: { language: "EN / 中文", back_home: "Back to home" },
    consultant: {
      eyebrow: "DIRECT TO A REAL CONSULTANT",
      title: "Scan, chat, get a real answer.",
      body: "Learning consultants reply fastest on WeChat or Line. Scan a QR with your phone — we usually reply within a few hours during studio hours.",
      points: [
        "Quick placement advice — IB / A-Level / KET·PET·FCE / K-12",
        "Pricing & schedule questions",
        "Trial-class follow-ups after a booking",
      ],
      scan_wechat: "Open WeChat → tap +  → Scan",
      scan_line: "Open Line → Add friend → Scan",
      open_in_line: "Open in Line",
      or_email: "OR EMAIL US",
      copied: "Copied — paste it in the app.",
    },
    portal: {
      title: "Student portal",
      welcome: "Welcome",
      your_announcements: "Your announcements",
      your_classes: "Upcoming classes",
      coming_soon: "Coming soon events",
      no_upcoming: "No upcoming interviews or trial classes — book one any time.",
      book_now: "Book now",
      past_history: "Past bookings",
    },
    achievements: {
      eyebrow: "06 · STUDENT ACHIEVEMENTS",
      title: "Voices we celebrate this term.",
      sub: "Weekly award winners and special studio honours.",
      weekly_eyebrow: "WEEKLY AWARDS",
      weekly_title: "Voices of the Week",
      special_eyebrow: "SPECIAL HONOURS",
      special_title: "Studio Awards",
      empty: "No awards posted yet — check back soon.",
    },
    news: {
      eyebrow: "07 · STUDIO NEWS",
      title: "Bi-weekly studio bulletin.",
      sub: "PDF issues with classroom highlights, faculty notes, and upcoming events.",
      issue: "Issue",
      issue_generic: "Latest issue",
      read_pdf: "Read PDF",
    },
    gallery: {
      eyebrow: "PHOTO ZONE",
      title: "Snapshots from the studio.",
      sub: "Classroom moments, term celebrations, and student work — curated by the studio.",
    },
    reading: {
      eyebrow: "FREE · 30-DAY READING CHALLENGE",
      title_a: "Get the Dawnville",
      title_b: "Reading Challenge planner.",
      sub: "A printable 30-day plan tailored by age — daily prompts, a teacher-curated book list, and a parent guide. Sign up and we'll send the planner the same day.",
      points: [
        "Age-banded book lists (Primary / Teens / K-12 / IB·A-Level)",
        "One daily prompt, ~15 minutes",
        "Weekly tips for parents — never spammy",
      ],
      name: "Your name",
      email: "Your email",
      age_band: "Child's stage",
      consent: "I'd like the planner and weekly reading tips. I can unsubscribe anytime.",
      submit: "Send me the planner",
      success_title: "You're in.",
      success_body: "We'll send the planner the same day you sign up — check your inbox.",
      already_title: "You're already on the list.",
      already_body: "Look for the planner in your inbox, or check spam.",
      download_body: "Your printable 30-day planner is ready right now. We've also saved your email so we can send weekly reading tips.",
      download_btn: "Download my planner (PDF)",
      download_hint: "Tip: print double-sided in colour, stick it on the fridge.",
      download_again_btn: "Download planner again",
      stages: { primary: "Primary (5–11)", teens: "Teens (11–15)", k12: "K-12 / High school", ib_alevel_cpe: "IB / A-Level / Cambridge" },
    },
  },
  zh: {
    nav: { booking: "预约", schedules: "课程表", announcements: "公告", programs: "学习项目", contact: "联系", portal: "学生门户", admin: "管理", login: "登录", admin_login: "管理登录", staff_login: "教职员", logout: "退出" },
    hero: {
      eyebrow: "DAWNVILLE · 英文工作室 · 2018 创立",
      title_a: "用思考者的方式",
      title_b: "学英文。",
      sub: "精品在线英文工作室，覆盖小学至 A-Level、IB 与剑桥精通级 CPE。小班教学、过程透明，每一封邮件都会有回应。",
      cta_book: "预约试听课",
      cta_explore: "浏览学习项目",
    },
    booking: {
      eyebrow: "01 · 预约课程",
      title: "预约第一次沟通",
      sub: "可选择面谈、免费试听课，或学习顾问咨询。我们将在 1 个工作日内邮件确认。",
      tab_interview: "面谈",
      tab_trial: "试听课",
      tab_consultant: "学习顾问",
      name: "姓名",
      email: "邮箱",
      phone: "电话 / 微信",
      program: "意向项目",
      date: "意向日期",
      time: "意向时段",
      notes: "想让我们提前了解的事项（选填）",
      submit: "确认预约",
      success: "预约已收到 — 我们会尽快联系您。",
      pick_date: "选择日期",
      pick_date_first: "请先选择日期 — 我们会显示当天可预约的时段。",
      no_slots_day: "{day} 暂无可预约时段，请改选其他日期或通过微信联系我们。",
      no_slots_global: "暂未设置预约时段，请通过微信 / Line 联系我们确认时间。",
      cal: {
        holiday: "国定假日",
        off_day: "工作室休息日",
        activity: "校内活动",
        full_week: "本周面谈名额已满",
        legend_available: "可预约",
        legend_holiday: "国定假日（休息）",
        legend_off: "Dawnville 休息日",
        legend_activity: "校内活动",
        legend_full: "面谈周已满（每周上限 {cap}）",
      },
      thanks_eyebrow: "已收到预约",
      thanks_title: "感谢您 — 已经收到！",
      thanks_body: "老师将于一个工作日内通过微信 / Line / 邮件与您确认时间。等待期间，请先领取您的免费阅读计划。",
      book_another: "再预约一次",
      gift_eyebrow: "等候期间的小礼物",
      gift_title: "30 天阅读挑战",
      gift_body: "可打印的每日阅读计划与启程书单，让孩子在试听课前先动起来。",
      gift_btn: "立即下载阅读计划 (PDF)",
      gift_hint: "已根据您选择的项目定制：{program}",
    },
    schedules: {
      eyebrow: "02 · 本学期",
      title: "正在进行的课程表",
      sub: "标记为「开放」的班级本学期仍接收新生。",
      th_class: "课程", th_program: "项目", th_teacher: "授课老师", th_day: "上课日", th_time: "时段", th_level: "级别", th_seats: "余位",
      open: "开放",
      full: "已满",
    },
    announcements: {
      eyebrow: "03 · 公告大道",
      title: "公告栏",
      sub: "工作室通知、日历更新、以及教师团队的话。",
      tab_public: "公开",
      tab_staff: "教职",
      tab_students: "学生与家长",
      empty: "暂无公告。",
    },
    programs: {
      eyebrow: "04 · 学习项目",
      title: "围绕考试、级别与里程碑设计。",
      sub: "每个项目由具备相应考官培训背景的学科负责人带领。",
    },
    contact: {
      eyebrow: "05 · 联系我们",
      title: "和真实的人聊聊。",
      address_label: "工作室", wechat_label: "微信", line_label: "Line", email_label: "邮箱", hours_label: "时间",
      recommended: "推荐",
      address: "Unit 4B, 灯笼街 88 号 · 在线 / 线下混合",
      wechat: "TwT02648",
      line: "misupig0423",
      email: "dawnvilleas2026@gmail.com",
      hours: "周一至周六 · 09:00 – 20:00",
    },
    auth: {
      login_title: "欢迎回来",
      login_sub: "登录访问 Dawnville 学生门户。",
      admin_login_title: "管理员登录",
      admin_login_sub: "管理公告、课程、预约、线索与审计事件。",
      register_title: "注册 Dawnville 账户",
      register_sub: "学生与家长可注册，访问门户全部功能。",
      email: "邮箱",
      password: "密码",
      name: "姓名",
      role: "我是…",
      role_student: "学生",
      role_parent: "家长",
      submit_login: "登录",
      submit_register: "创建账户",
      switch_to_register: "还没有账户？立即注册",
      switch_to_login: "已有账户？登录",
      forgot_link: "忘记密码？",
      bad_creds: "邮箱或密码错误。",
    },
    forgot: {
      title: "重置密码",
      sub: "输入您的账户邮箱。若已注册，系统将生成重置链接（管理员也可从服务器日志获取）。",
      submit: "发送重置链接",
      sent_title: "请查收邮箱",
      sent_body: "若该邮箱已注册，重置链接已生成，1 小时内有效。",
      back_to_login: "返回登录",
    },
    reset: {
      title: "设置新密码",
      sub: "请输入新密码 — 至少 6 个字符。",
      new_password: "新密码",
      confirm_password: "再次输入新密码",
      submit: "更新密码",
      success: "密码已更新，请重新登录。",
      mismatch: "两次输入的密码不一致。",
      too_short: "密码至少 6 个字符。",
      no_token: "重置链接无效或缺失。",
    },
    admin: {
      title: "管理 · 公告面板",
      sub: "发布新公告，选择目标公告板。",
      title_field: "标题 (英文)",
      title_zh_field: "标题 (中文, 选填)",
      content_field: "正文 (英文)",
      content_zh_field: "正文 (中文, 选填)",
      audience: "目标公告板",
      audience_public: "公开",
      audience_staff: "教职",
      audience_students: "学生与家长",
      post: "发布公告",
      recent: "最近发布",
      delete: "删除",
      bookings: "最近预约",
      tab_ann: "公告",
      tab_schedules: "课程",
      tab_course_calendar: "课程日历",
      tab_bookings: "预约",
      tab_slots: "可预约时段",
      tab_calendar: "日历",
      tab_hero: "首页内容",
      tab_programs: "学习项目",
      tab_gallery: "影像",
      tab_achievements: "学员风采",
      tab_news: "工作室通讯",
      tab_planners: "阅读手册",
      tab_cefr: "CEFR 测试",
      tab_students: "学生档案",
      tab_notices: "课程通知",
      tab_mock: "模拟测试",
      tab_staff: "教职员",
      tab_payslips: "工资单",
      tab_leads: "线索",
      tab_audit: "审计日志",
    },
    common: { language: "EN / 中文", back_home: "返回首页" },
    consultant: {
      eyebrow: "直接联系学习顾问",
      title: "扫码加好友，立即沟通。",
      body: "学习顾问在微信与 Line 上回复最快，请用手机扫码添加好友。工作室时间内通常几小时内回复。",
      points: [
        "选班建议 — IB / A-Level / KET·PET·FCE / K-12",
        "课程价格与时段咨询",
        "预约试听后的跟进",
      ],
      scan_wechat: "打开微信 → 右上角 + → 扫一扫",
      scan_line: "打开 Line → 加好友 → 扫描",
      open_in_line: "在 Line 中打开",
      or_email: "或邮件联系",
      copied: "已复制，请粘贴到应用内。",
    },
    portal: {
      title: "学生门户",
      welcome: "欢迎",
      your_announcements: "你的公告",
      your_classes: "近期课程",
      coming_soon: "即将到来的活动",
      no_upcoming: "暂无即将进行的面谈或试听课 — 随时可预约。",
      book_now: "立即预约",
      past_history: "历史预约",
    },
    achievements: {
      eyebrow: "06 · 学员风采",
      title: "本学期值得喝彩的声音。",
      sub: "每周奖学员与特别荣誉。",
      weekly_eyebrow: "每周奖项",
      weekly_title: "每周之声",
      special_eyebrow: "特别荣誉",
      special_title: "工作室奖项",
      empty: "暂无奖项发布。",
    },
    news: {
      eyebrow: "07 · 工作室通讯",
      title: "Dawnville 半月刊。",
      sub: "PDF 通讯：课堂精选、教师笔记与即将举办的活动。",
      issue: "第",
      issue_generic: "最新一期",
      read_pdf: "阅读 PDF",
    },
    gallery: {
      eyebrow: "影像分享",
      title: "工作室的瞬间。",
      sub: "课堂、学期庆典、学员作品 — 由工作室甄选。",
    },
    reading: {
      eyebrow: "免费 · 30 天阅读挑战",
      title_a: "领取 Dawnville",
      title_b: "30 天阅读计划。",
      sub: "可打印的 30 天阅读计划，按年龄定制 — 每日提示、教师精选书单与家长指南。注册当天即可收到。",
      points: [
        "按年龄段精选书单（小学 / 青少年 / K-12 / IB·A-Level）",
        "每日 1 个提示，约 15 分钟",
        "每周一封家长建议邮件 — 绝不打扰",
      ],
      name: "您的姓名",
      email: "您的邮箱",
      age_band: "孩子所处阶段",
      consent: "我希望收到阅读计划与每周邮件，随时可退订。",
      submit: "把计划寄给我",
      success_title: "已加入！",
      success_body: "请稍后查收邮箱 — 阅读计划将于报名当天发送。",
      already_title: "您已在订阅名单。",
      already_body: "请到邮箱查收计划，或检查垃圾邮件。",
      download_body: "可立即下载 30 天阅读计划 PDF，我们也会将每周阅读建议邮件寄给您。",
      download_btn: "立即下载阅读计划 (PDF)",
      download_hint: "建议彩色双面打印，贴在冰箱上。",
      download_again_btn: "再次下载阅读计划",
      stages: { primary: "小学 (5–11岁)", teens: "青少年 (11–15岁)", k12: "K-12 / 高中", ib_alevel_cpe: "IB / A-Level / 剑桥" },
    },
  },
};

export const programsData = [
  { id: "k12", label_en: "K-12", label_zh: "K-12", desc_en: "Long-form curriculum aligning U.S. K-12 English Language Arts with college-prep writing.", desc_zh: "对标美国 K-12 英语语文，长线培养至升学写作。" },
  { id: "ib", label_en: "IB English", label_zh: "IB 英文", desc_en: "Standard & Higher Level. Paper 1/2 strategy, IO oral, Extended Essay supervision.", desc_zh: "SL & HL。Paper 1/2 战术、IO 口试、EE 论文督导。" },
  { id: "cambridge", label_en: "Cambridge KET/PET/FCE", label_zh: "剑桥 KET/PET/FCE", desc_en: "A2 to B2 Cambridge English: Key, Preliminary, and First. Examiner-trained faculty, mock cycles every 3 weeks.", desc_zh: "A2 至 B2 级别（KET / PET / FCE）。具考官培训背景的教师，每三周一次模考。" },
  { id: "alevel", label_en: "A-Level English", label_zh: "A-Level 英文", desc_en: "Edexcel & CIE Literature and Language. Coursework + exam track support.", desc_zh: "Edexcel & CIE 文学与语言。Coursework 与考试双路径。" },
  { id: "teens", label_en: "Teens", label_zh: "青少年", desc_en: "Ages 11–15. Debate, journalism, and academic English through projects.", desc_zh: "11–15 岁。通过辩论、新闻、项目式学习提升学术英文。" },
  { id: "primary", label_en: "Primary", label_zh: "小学", desc_en: "Phonics, reading fluency, story writing — confident voices from day one.", desc_zh: "自然拼读、阅读流畅度、故事写作 — 从第一天起就有声音。" },
];

export function LangProvider({ children }) {
  const [lang, setLang] = useState(() => localStorage.getItem("dv-lang") || "en");
  useEffect(() => { localStorage.setItem("dv-lang", lang); }, [lang]);
  const toggle = () => setLang((l) => (l === "en" ? "zh" : "en"));
  const t = dict[lang];
  return <LangContext.Provider value={{ lang, setLang, toggle, t }}>{children}</LangContext.Provider>;
}

export function useLang() {
  const ctx = useContext(LangContext);
  if (!ctx) throw new Error("useLang must be used within LangProvider");
  return ctx;
}
